import * as React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Button, Modal, Toast } from 'react-bootstrap';

import { useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { Typeahead } from 'react-bootstrap-typeahead';

import { allDepartmentDropdown, checkForAssetNumber, createAuditMutation, GET_REQUEST_FY_BY_BARCODE, updateTransferMutation } from './api';
import * as selectDropdowns from '../RequestForms/utils';
import { Comments } from '../Comments';
import Statuses from '../RequestForms/components/Statuses';
import { TooltipButton } from '../TooltipButton';

export const TransferApprovalForm = (props: any) => {
  const [comment, setComment] = React.useState('');
  const [createAudit] = useMutation(createAuditMutation);
  const [updateEOne] = useMutation(updateTransferMutation);
  const [toastVisible, setToastVisible] = React.useState(false);
  const [elevated, setElevated] = React.useState(false);
  const { user } = useSelector((state: any) => ({
    user: state.user,
  }));
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    let enableDisabledFields = false;
    if (user.roles) {
      user.roles.forEach((role) => {
        switch (role.roleId) {
          case 1: // admin
          case 3: // pc - non cap
          case 10: // pc - cap
          case 18: // rec
          case 19: // rec - cap
          case 20: // rec super
          case 21: // rec super - ca
            enableDisabledFields = true;
        }
      });
    }
    setElevated(enableDisabledFields);
  }, []);

  const {
    register,
    handleSubmit,
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      assetNumber: props.queryData.assetNumber ? props.queryData.assetNumber : '',
      barCode: props.queryData.barCode,
      description1: props.queryData.description1,
      description2: props.queryData.description2,
      description3: props.queryData.description3,
      additionalRemarks: props.queryData.additionalRemarks,
      additionalRemarks2: props.queryData.additionalRemarks2,
      additionalRemarks3: props.queryData.additionalRemarks3,
      physicalOwner: props.queryData.physicalOwner ? props.queryData.physicalOwner : null,
      businessUnit: props.queryData.businessUnit,
      department: props.queryData.department,
      location: props.queryData.location,
      updateStatus: props.queryData.updateStatus,
      id: props.queryData.id,
    },
  });

  const {
    data: assetNumberData,
    error: assetNumberError,
    loading: assetNumberLoading,
  } = useQuery(checkForAssetNumber, {
    variables: {
      barCode: props.queryData.barCode,
      serialNumber: props.queryData.serialNumber,
    },
  });

  const updateAssetNumber = () => {
    setValue('assetNumber', assetNumberData.readFixedAsset.assetNumber.toString())
    setVisible(false)
  };

  const { data } = useQuery(GET_REQUEST_FY_BY_BARCODE, {
    variables: { barcode: props.queryData.barCode },
  });

  const { data: deptData } = useQuery(allDepartmentDropdown);

  const createAuditForTransfer = (formValues, update?: boolean) => {
    let commentStr =
      comment !== '' ? comment : update ? `Transfer of barcode ${formValues.barCode} updated` : `Transfer of barcode ${formValues.barCode} completed`;
    let salvageCommentStr =
      comment !== ''
        ? comment
        : update
          ? `Transfer of barcode ${formValues.barCode} updated`
          : `Transfer of barcode ${formValues.barCode} complete. Salvage now pending disposal approval.`;
    const audit = {
      barcode: formValues.barCode,
      createdBy: user.adId,
      lastUpdatedBy: user.adId,
      comment: props.queryData.equipmentStatus !== null ? salvageCommentStr : commentStr,
      updatedOn: new Date(),
      entryType: props.queryData.equipmentStatus !== null ? 'Salvage' : formValues.updateStatus,
    };
    createAudit({ variables: audit }).catch((err) =>
      console.log('Error: /frontend/src/components/AssetRelocationForms//createAuditForTransfer', err)
    );
  };

  const deptDropdown = () => {
    if (!deptData) {
      return [];
    }

    return deptData.readDepartments;
  };

  const onSubmit = handleSubmit(async (data) => {
    updateEOne({
      variables: {
        input: data,
        user: user.adId,
      },
    })
      .then(async (res) => {
        if (res.data.updateTransfer.id === -1) {
          throw Error('Error updating asset transfer!');
        }
      })
      .then(() => {
        createAuditForTransfer(data);
        setToastVisible(!toastVisible);
      })
      .catch((err) => {
        alert(err);
      })
      .finally(() => {
        // alert('Transfer Completed Successfully, redirecting...')
        setTimeout(() => {
          window.history.back();
        }, 1500);
      });
  });

  /**
   * Used to update form data without sending email to prop coord
   * or approving transfer
   */
  const handleUpdate = handleSubmit(async (data) => {
    updateEOne({
      variables: {
        input: data,
        update: true,
        user: user.adId,
      },
    })
      .then(async (res) => {
        if (res.data.updateTransfer.id === -1) {
          throw Error('Error updating asset transfer!');
        }
      })
      .then(() => {
        createAuditForTransfer(data, true);
        setToastVisible(!toastVisible);
      })
      .catch((err) => {
        alert(err);
      })
      .finally(() => {
        // alert('Transfer Completed Successfully, redirecting...')
        setTimeout(() => {
          window.history.back();
        }, 1500);
      });
  });

  return (
    <React.Fragment>
      <form id='transferForm' onSubmit={onSubmit}>
        {props.queryData.assetNumber ? (
          <div>
            <h2>Complete Transfer for Asset {props.queryData.assetNumber}</h2>
            {props.queryData.equipmentStatus !== null && <h4>This transfer is part of the salvage process for this item</h4>}
          </div>
        ) : (
          <div>
            <h2>Complete Transfer</h2>
          </div>
        )}
        <div className='row justify-content-start'>
          <div className='col-sm-9 mt-2'>
            {(props.queryData.updateStatus === 'Transfer Installed, Pending Approval' || props.queryData.updateStatus === 'Salvage @ OIT') && (
              <Button type='submit' variant='info' form='transferForm' className='float-left mr-2'>
                Update and Approve Transfer
              </Button>
            )}
            {elevated && props.queryData.updateStatus !== 'Ready for EOne' && (
              // <Button variant='outline-primary' className='float-left mr-2' onClick={handleUpdate}>
              //   Update
              // </Button>
              <TooltipButton
                show={0}
                tooltipLabel='This button does NOT approve a transfer. It is intended for changing values before approving a transfer.'
                variant='outline-primary'
                buttonClass='float-left mr-2'
                onClick={handleUpdate}
                buttonLabel='Update'
              />
            )}
            {elevated && !assetNumberError && !assetNumberLoading && (
              <>
                <Button onClick={() => setVisible(true)} className='float-left mr-2' variant='danger'>
                  Check Asset Number
                </Button>
                <Modal dialogClassName='modal-half-width' show={visible} onHide={() => setVisible(false)}>
                  <Modal.Header>
                    <Modal.Title>Review Asset Information</Modal.Title>
                    <div className='float-right'>
                      <Button variant='info' onClick={() => updateAssetNumber()}>
                        {/* {submitting && <Spinner className='mr-1' as='span' animation='border' size='sm' role='status' aria-hidden='true' />} */}
                        Apply Asset Number?
                      </Button>
                      <Button className='ml-2 float-right' onClick={() => setVisible(false)}>
                        Close
                      </Button>
                    </div>
                  </Modal.Header>
                  <Modal.Body>
                    <div>
                      <h5>{`Asset Number: ${assetNumberData.readFixedAsset.assetNumber}`}</h5>
                      <h5>{`Barcode: ${assetNumberData.readFixedAsset.barCode}`}</h5>
                      <h5>{`Serial Number: ${assetNumberData.readFixedAsset.serialNumber}`}</h5>
                    </div>
                  </Modal.Body>
                </Modal>
              </>
            )}

            <Button onClick={() => window.history.back()} variant='primary' className='float-left mr-2'>
              Close
            </Button>
            {data && data.readRequestFYByBarcode && (
              <Button
                className='mr-2'
                // style={{width: '50%'}}
                variant='danger'
                id='reqNumber'
                onClick={() => {
                  window.open(`/receiving/view/requestId=${data.readRequestFYByBarcode.requestId}`);
                }}>{`View Request: ${data.readRequestFYByBarcode.requestNumber}`}</Button>
            )}
          </div>
          <div className='col-sm-3 mt-2'>
            <Controller
              control={control}
              name='updateStatus'
              render={({ field: { onChange, value } }) => (
                <Statuses noLabel onChange={onChange} idName='updateStatus' value={value} selClass='form-control' lblClass='' />
              )}
            />
          </div>
        </div>
        <Toast
          autohide
          style={{ backgroundColor: '#454545', color: '#ffffff', position: 'absolute', top: 75, right: '50%' }}
          show={toastVisible}
          onClose={() => setToastVisible(!toastVisible)}>
          <Toast.Body>Transfer Updated Successfully!</Toast.Body>
        </Toast>
        <div className='row'>
          <div className='col-sm-8'>
            <div className='row justify-content-start mt-3'>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>Asset Number</label>
                  <Controller
                    control={control}
                    name='assetNumber'
                    render={({ field: { name, onChange, value } }) => (
                      <input
                        style={{ backgroundColor: '#DDD', opacity: 1.0, cursor: 'not-allowed', pointerEvents: 'none' }}
                        className='form-control col-sm'
                        name={name}
                        onChange={onChange}
                        value={value}
                      />
                    )}
                  />
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>Barcode</label>
                  <Controller
                    control={control}
                    name='barCode'
                    render={({ field: { name, onChange, value } }) => (
                      <input className='form-control col-sm' disabled name={name} onChange={onChange} value={value} />
                    )}
                  />
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>Description 1</label>
                  <Controller
                    control={control}
                    name='description1'
                    render={({ field: { name, onChange, value } }) => (
                      <input maxLength={30} className='form-control col-sm' disabled name={name} onChange={onChange} value={value} />
                    )}
                  />
                </div>
              </div>
            </div>
            <div className='row justify-content-start mt-3'>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>Description 2</label>
                  <input maxLength={30} className='form-control col-sm' {...register('description2')} />
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>Description 3</label>
                  <input maxLength={30} className='form-control col-sm' {...register('description3')} />
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>Business Unit</label>
                  <input className='form-control col-sm' disabled {...register('businessUnit')} />
                </div>
              </div>
            </div>
            <div className='row justify-content-start mt-3'>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>Additional Remarks</label>
                  <input maxLength={30} className='form-control col-sm' {...register('additionalRemarks')} />
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>Additional Remarks 2</label>
                  <input maxLength={30} className='form-control col-sm' {...register('additionalRemarks2')} />
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>Additional Remarks 3</label>
                  <input maxLength={25} className='form-control col-sm' {...register('additionalRemarks3')} />
                </div>
              </div>
            </div>

            <div className='row justify-content-start mt-3'>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>
                    Department<span style={{ color: '#f00' }}>&nbsp;*</span>
                  </label>
                  <Controller
                    control={control}
                    name='department'
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                      <Typeahead
                        id='department'
                        labelKey={(option) => `${option.Abbreviation} - ${option.Name}`}
                        options={deptDropdown()}
                        onChange={(e) => onChange(e[0] ? e[0].Abbreviation : null)}
                      />
                    )}
                  />
                  <label>{`(Current: ${getValues().department})`}</label>
                  <span style={{ color: 'red' }}>
                    <br />
                    {errors.department && 'Department must have a value!'}
                  </span>
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>
                    Physical Owner<span style={{ color: '#f00' }}>&nbsp;*</span>
                  </label>
                  <Controller
                    control={control}
                    name='physicalOwner'
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                      <Typeahead
                        id='physicalOwner'
                        labelKey={(option) => `${option.Code} - ${option.Description}`}
                        options={selectDropdowns.physicalOwners}
                        onChange={(e) => onChange(e[0] ? e[0].Code.toString() : null)}
                      />
                    )}
                  />
                  <label>{`(Current: ${getValues().physicalOwner})`}</label>
                  <span style={{ color: 'red' }}>
                    <br />
                    {errors.physicalOwner && 'Physical Owner must have a value!'}
                  </span>
                </div>
              </div>
              <div className='col-sm-4'>
                <div className='form-group'>
                  <label>
                    Location
                    <span style={{ color: '#f00' }}>&nbsp;**</span>
                  </label>
                  <input className='form-control col-sm' {...register('location')} />
                </div>
              </div>
            </div>
            <div>
              <label>
                <span style={{ color: '#f00' }}>*&nbsp;</span>
                These items are required to confirm possession of this transferred item. If any of these required fields doesn't need to be changed,
                please re-type the current value
                <br />
                <span style={{ color: '#f00' }}>**&nbsp;</span>
                Changes to this item must be made using the Enterprise One website.
              </label>
            </div>
          </div>

          <div className='col-sm-4 mt-3'>
            <div className='form-group'>
              <label>Add Comment</label>
              <textarea className='form-control col-sm' id='comment' rows={1} value={comment} onChange={(e) => setComment(e.target.value)} />
            </div>
            <Comments barcode={props.queryData.barCode} style={{ height: '600px' }} />
          </div>
        </div>
      </form>
    </React.Fragment>
  );
};
